import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "@/components/Helmet";
import { PromoControls } from "@/components/portal/PromoControls";
import { CouponManager } from "@/components/portal/CouponManager";
import { AuthenticatedHeader } from "@/components/portal/AuthenticatedHeader";
import { CurrencyDock } from "@/components/portal/CurrencyDock";
import { LogoShield } from "@/components/LogoShield";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useRequireMfa } from "@/hooks/use-require-mfa";
import { supabase } from "@/integrations/supabase/client";

export default function Promotions() {
  const { user, loading } = useAuth();
  const { checking } = useRequireMfa();
  const navigate = useNavigate();

  const { data: isAdmin, isLoading: isAdminLoading } = useQuery({
    queryKey: ["isAdmin", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user!.id)
        .eq("role", "admin")
        .maybeSingle();
      return !!data;
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (!checking && !loading && isAdmin === false) {
      navigate("/portal");
    }
  }, [checking, loading, isAdmin, navigate]);

  if (checking || loading || isAdminLoading || isAdmin === undefined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary">Loading promotions...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Promotions | ShadowScan Portal</title>
        <meta name="description" content="Manage promotion banners and Stripe coupon codes for ShadowScan." />
      </Helmet>
      <AuthenticatedHeader
        userEmail={user?.email || ""}
        context="admin"
        roleLabel="Admin"
        showLiveScan={true}
        showPromotions={true}
        leftSlot={(
          <div className="flex items-center gap-3">
            <LogoShield className="h-7 w-7" />
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-mono text-muted-foreground">ShadowScan Portal</span>
              <span className="text-xl font-heading font-bold tracking-tight">Admin Console</span>
            </div>
          </div>
        )}
        centerSlot={(
          <Button variant="ghost" size="sm" onClick={() => navigate("/portal/admin")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Admin console home
          </Button>
        )}
      />
      <main className="container mx-auto px-4 py-10 space-y-8">
        <PromoControls />
        <CouponManager />
      </main>
      <CurrencyDock />
    </div>
  );
}
